import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { UserFragment } from '@/graphql/fragments/generated/User';
import { ILearningContent } from '@/modules/corporate/components/LearningContent/types';
import { ContentEntity, EventUnion, GroupTypeEnum } from '@/types/schema';

import { useAddContentsToGroupMutation } from '../graphql/mutations/generated/addContentsToGroup';
import { useAddUsersToGroupMutation } from '../graphql/mutations/generated/addUsersToGroup';
import { ActionDialog, DialogActions, UserGroupsActions } from '../types';

function useGroupActions({ groupId, type }: { groupId: string; type: GroupTypeEnum }) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>('');
  const [inViewActionDialog, setInViewActionDialog] = useState<ActionDialog | null>(null);
  const [addUserToGroupResult, executeAddUserToGroup] = useAddUsersToGroupMutation();
  const [addContentsToGroupResult, executeAddContentsToGroup] = useAddContentsToGroupMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();

  const onAction = useCallback(
    (actionType: UserGroupsActions) => {
      switch (actionType) {
        case UserGroupsActions.Add:
          setInViewActionDialog(ActionDialog.Add);
          break;
        default:
          return null;
      }
    },
    [setInViewActionDialog],
  );

  const onAddUsers = useCallback(
    async (userIds: string[], users?: UserFragment[]) => {
      const result = await executeAddUserToGroup({
        data: {
          groupId,
          userIds,
        },
      });

      if (!result || result.error) {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      } else {
        showSnackbar({
          message: (
            <Trans
              i18nKey={'Group:Details.LearnersAddedToGroup'}
              values={{
                count: userIds.length,
                name: users?.[0]?.name,
              }}
            />
          ),
        });
        setInViewActionDialog(null);
      }
    },
    [executeAddUserToGroup, groupId, showErrorSnackbar, t, showSnackbar],
  );

  const onAddContents = useCallback(
    async (entityIds: string[], contents?: ILearningContent[]) => {
      const result = await executeAddContentsToGroup({
        data: {
          groupId,
          entityIds,
        },
      });

      if (!result || result.error) {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      } else {
        showSnackbar({
          message: (
            <Trans
              i18nKey={
                entityIds.length === 1
                  ? 'Group:Details.ContentAddedToCollection'
                  : 'Group:Details.ContentsAddedToCollection'
              }
              values={{
                count: entityIds.length - 1,
                name: contents?.[0]?.name,
              }}
            />
          ),
        });
        setInViewActionDialog(null);
      }
    },
    [executeAddContentsToGroup, groupId, showErrorSnackbar, t, showSnackbar],
  );

  const onDialogAction = useCallback(
    (
      dialogAction: DialogActions,
      ids?: string[],
      items?: (UserFragment | EventUnion | ContentEntity)[],
    ) => {
      switch (dialogAction) {
        case DialogActions.Close:
          setInViewActionDialog(null);
          break;
        case DialogActions.Add:
          type === GroupTypeEnum.UserCollection
            ? onAddUsers(ids!, items as UserFragment[])
            : onAddContents(ids!, items as ILearningContent[]);
          break;
        default:
          break;
      }
    },
    [onAddContents, onAddUsers, type],
  );

  return {
    state: {
      inViewActionDialog,
      searchText,
      fetching: addUserToGroupResult.fetching || addContentsToGroupResult.fetching,
    },
    methods: {
      setSearchText,
      onAction,
      onDialogAction,
    },
  };
}

export default useGroupActions;
